import React from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, Loader2, ArrowLeft, RotateCcw, LayoutGrid, Save } from 'lucide-react';
import Modal from '../components/ui/Modal';
import Dialog from '../components/ui/Dialog';
import Input from '../components/ui/Input';
import { Position, getJSTDateString } from '../api';
import ShiftCreationTimeline from '../components/shifts/ShiftCreationTimeline';
import { useShiftCreation } from '../hooks/useShiftCreation';
import { DutyMarks, dutyMark } from '../components/StaffDuties';
import { gapsLabel, fmtMinutes } from '../utils/coverage';
import { rangeLabel } from '../utils/shiftTime';

const STEPS = [
    { id: 0, label: '計画', description: '募集枠を作成' },
    { id: 1, label: '募集', description: 'スタッフへ依頼' },
    { id: 2, label: '調整', description: 'シフト割り当て' },
    { id: 3, label: '公開', description: '確定・通知' },
];

const getBorderColorClass = (posId: string | undefined, positions: Position[]) => {
    if (!posId) return 'border-gray-200';
    const pos = positions.find(p => p.id === posId);
    if (!pos) return 'border-gray-200';
    const bgClass = pos.color.split(' ')[0] || '';
    const colorMap: Record<string, string> = {
        'bg-blue-100': 'border-blue-500', 'bg-green-100': 'border-green-500', 'bg-red-100': 'border-red-500',
        'bg-yellow-100': 'border-yellow-500', 'bg-purple-100': 'border-purple-500', 'bg-orange-100': 'border-orange-500',
        'bg-gray-100': 'border-gray-500', 'bg-teal-100': 'border-teal-500', 'bg-pink-100': 'border-pink-500',
    };
    return colorMap[bgClass] || 'border-gray-300';
};

const POSITION_COLORS = ['bg-blue-100 text-blue-800', 'bg-green-100 text-green-800', 'bg-orange-100 text-orange-800', 'bg-purple-100 text-purple-800', 'bg-pink-100 text-pink-800', 'bg-teal-100 text-teal-800', 'bg-yellow-100 text-yellow-800', 'bg-red-100 text-red-800'];

const WARNING_STYLE: Record<string, { label: string; cls: string }> = {
    shortage: { label: '人数不足', cls: 'bg-red-50 text-red-600' },
    duty_missing: { label: '業務の対応者なし', cls: 'bg-rose-50 text-rose-700' },
    ng_conflict: { label: '相性NG', cls: 'bg-rose-50 text-rose-700' },
    pair_missing: { label: 'ペア不在', cls: 'bg-rose-50 text-rose-700' },
    duty_over: { label: '業務の重なり', cls: 'bg-orange-50 text-orange-600' },
    over_limit: { label: '上限超え', cls: 'bg-orange-50 text-orange-600' },
    consecutive: { label: '連勤', cls: 'bg-amber-50 text-amber-700' },
    no_expert: { label: '経験者不在', cls: 'bg-purple-50 text-purple-700' },
};

const ShiftCreationPage: React.FC = () => {
  const sc = useShiftCreation();
  // 警告は日付ごとに1行にまとめる（日付のない警告は「期間全体」）
  const [openWarningDay, setOpenWarningDay] = React.useState<string | null>(null);
  const warningGroups = React.useMemo(() => {
      const map = new Map<string, typeof sc.warnings>();
      sc.warnings.forEach(w => { const k = w.date || 'all'; map.set(k, [...(map.get(k) || []), w]); });
      const youbi = ['日', '月', '火', '水', '木', '金', '土'];
      return Array.from(map.entries()).sort(([a], [b]) => (a === 'all' ? -1 : b === 'all' ? 1 : a.localeCompare(b))).map(([key, items]) => {
          const kinds = Array.from(new Set(items.map(w => w.kind))).map(kind => ({ kind, count: items.filter(w => w.kind === kind).length }));
          const d = key === 'all' ? null : new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
          return { key, date: key === 'all' ? undefined : key, label: d ? `${d.getMonth() + 1}/${d.getDate()}（${youbi[d.getDay()]}）` : '期間全体', kinds, items };
      });
  }, [sc.warnings]);
  const [isAddPositionOpen, setIsAddPositionOpen] = React.useState(false);
  const [newPositionName, setNewPositionName] = React.useState('');
  const [newPositionColor, setNewPositionColor] = React.useState(POSITION_COLORS[0]);
  const handleAddPosition = async () => {
      if (await sc.addPosition(newPositionName, newPositionColor)) {
          setIsAddPositionOpen(false);
          setNewPositionName('');
      }
  };

  return (
    <div className="animate-in">
      <Dialog {...sc.dialogState} isLoading={sc.isSending} onCancel={() => sc.setDialogState(prev => ({ ...prev, isOpen: false }))} />

      <Modal
          isOpen={sc.isPeriodModalOpen}
          onClose={() => {
              if (!sc.targetPeriod) {
                  sc.navigate('/admin/shifts');
              } else {
                  sc.setIsPeriodModalOpen(false);
              }
          }}
          title="シフト作成期間の設定"
          maxWidth="max-w-md"
      >
          <div className="space-y-6 pt-2">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <div className="text-xs text-blue-800 font-bold leading-relaxed">シフトを作成する期間を指定してください。</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="text-[10px] font-bold text-slate-400 mb-1 block">開始日</label>
                      <input
                        type="date"
                        value={sc.setupStartDate}
                        min={sc.todayStr}
                        onChange={e => {
                            const newStart = e.target.value;
                            sc.setSetupStartDate(newStart);
                            if (sc.setupEndDate && newStart > sc.setupEndDate) {
                                sc.setSetupEndDate(newStart);
                            }
                        }}
                        className="w-full border rounded-[4px] p-2 text-sm font-bold"
                      />
                  </div>
                  <div>
                      <label className="text-[10px] font-bold text-slate-400 mb-1 block">終了日</label>
                      <input
                        type="date"
                        value={sc.setupEndDate}
                        min={sc.setupStartDate || sc.todayStr}
                        onChange={e => sc.setSetupEndDate(e.target.value)}
                        className="w-full border rounded-[4px] p-2 text-sm font-bold"
                      />
                  </div>
              </div>

              <div className="pt-4 flex flex-col gap-2">
                  <button onClick={sc.handleConfirmPeriod} disabled={!sc.setupStartDate || !sc.setupEndDate || sc.isLoading} className="w-full py-3 bg-blue-600 text-white font-bold rounded-[4px] shadow-lg flex items-center justify-center hover:bg-blue-700 disabled:bg-slate-300 transition-all">{sc.isLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : '作成を開始する'}</button>

                  {sc.targetPeriod ? (
                      <div className="flex flex-col gap-2 pt-4 border-t border-slate-100">
                        <button onClick={() => sc.setIsPeriodModalOpen(false)} className="w-full py-2.5 text-slate-500 text-xs font-bold bg-slate-50 rounded hover:bg-slate-100 transition-colors">キャンセル（変更しない）</button>
                        <button onClick={sc.handleResetPeriod} className="w-full py-2 text-red-500 text-[10px] font-bold flex items-center justify-center gap-1 hover:underline mt-2">
                            <RotateCcw size={12} />
                            期間設定を解除して最初からやり直す
                        </button>
                      </div>
                  ) : (
                      <button onClick={() => sc.navigate('/admin/shifts')} className="w-full py-2.5 text-slate-400 text-xs font-bold flex items-center justify-center gap-1 hover:text-slate-600">
                          <ArrowLeft size={14} />
                          シフト確認画面に戻る
                      </button>
                  )}
              </div>
          </div>
      </Modal>

      <div className="bg-white border-b border-gray-200 mb-4 sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-3">
              {/* ステップ表示（クリックで移動。先へは順に進む） */}
              <div className="flex flex-wrap items-center gap-2" data-testid="stepper">
                  {sc.stores.length > 1 && (
                      <select value={sc.selectedStoreId} onChange={(e) => sc.handleChangeStore(e.target.value)} aria-label="店舗" data-testid="creation-store" className="mr-2 bg-white border border-slate-300 rounded-[4px] px-3 py-1.5 text-sm font-bold text-slate-700 outline-none">
                          {sc.stores.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                      </select>
                  )}
                  {STEPS.map((st, i) => {
                      const state = i < sc.currentStep ? 'done' : i === sc.currentStep ? 'current' : 'todo';
                      return (
                          <React.Fragment key={st.id}>
                              <button type="button" onClick={() => sc.goToStep(i)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold whitespace-nowrap transition-colors ${state === 'current' ? 'bg-blue-600 border-blue-600 text-white' : state === 'done' ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${state === 'current' ? 'bg-white text-blue-600' : state === 'done' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{i + 1}</span>
                                  <span>{st.label}</span>
                                  <span className={`hidden md:inline text-[10px] font-medium ${state === 'current' ? 'text-blue-100' : 'text-slate-400'}`}>{st.description}</span>
                              </button>
                              {i < STEPS.length - 1 && <span className="text-slate-300 text-xs">›</span>}
                          </React.Fragment>
                      );
                  })}
              </div>

              <div className="flex flex-col md:flex-row md:flex-wrap md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 w-full md:w-auto shrink-0">
                       <button onClick={() => sc.handleDayNavigation('prev')} disabled={!sc.targetPeriod || sc.currentDate <= sc.targetPeriod.start} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 disabled:opacity-30"><ChevronLeft size={20}/></button>
                       <div>
                           <div className="flex items-center gap-3">
                               <h1 className="text-lg font-bold text-slate-800 whitespace-nowrap">{sc.formatDateJP(sc.currentDate)}</h1>
                               <div className="flex items-center gap-2">
                                   {sc.targetPeriod && (
                                       <button onClick={() => { sc.setSetupStartDate(getJSTDateString(sc.targetPeriod!.start)); sc.setSetupEndDate(getJSTDateString(sc.targetPeriod!.end)); sc.setIsPeriodModalOpen(true); }} className="text-[10px] bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded text-slate-600 font-bold flex items-center transition-colors whitespace-nowrap">
                                           {sc.formatPeriodJP(sc.targetPeriod)}
                                           <span className="text-[8px] opacity-50 ml-1.5 font-bold">変更</span>
                                       </button>
                                   )}
                                   {sc.targetPeriod && sc.currentStep === 0 && (
                                       sc.unconfiguredDaysCount > 0 ? (
                                           <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-600 rounded-full border border-amber-100 whitespace-nowrap">
                                               <AlertCircle size={10} />
                                               <span className="text-[10px] font-black tracking-tight">未設定{sc.unconfiguredDaysCount}日</span>
                                           </div>
                                       ) : (
                                           <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full border border-green-100 whitespace-nowrap">
                                               <CheckCircle2 size={10} />
                                               <span className="text-[10px] font-black tracking-tight">全日設定済み</span>
                                           </div>
                                       )
                                   )}
                               </div>
                           </div>
                       </div>
                       <button onClick={() => sc.handleDayNavigation('next')} disabled={!sc.targetPeriod || sc.currentDate >= sc.targetPeriod.end} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 disabled:opacity-30"><ChevronRight size={20}/></button>
                  </div>
                  <div className="flex-1 min-w-0 flex justify-end items-center gap-4">
                      <div className="flex flex-wrap gap-2 items-center justify-end">
                          {sc.currentStep === 0 && (
                              <>
                                <button onClick={sc.handleOpenSavePattern} disabled={sc.vacancies.length === 0} className={`px-4 py-2 font-bold rounded-lg text-xs flex items-center shadow-sm whitespace-nowrap shrink-0 border transition-all ${sc.vacancies.length > 0 ? 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50' : 'bg-gray-50 text-slate-300 border-gray-200 cursor-not-allowed'}`}>モデルシフトとして保存</button>
                                <button onClick={sc.handleToggleHoliday} className={`px-4 py-2 font-bold rounded-lg text-xs flex items-center shadow-sm whitespace-nowrap shrink-0 border transition-all ${sc.isTodayHoliday ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{sc.isTodayHoliday ? '休日設定解除' : '休日設定'}</button>
                                {sc.isDirty && (
                                  <button onClick={sc.handleTemporarySave} className="px-4 py-2 font-bold rounded-lg text-xs flex items-center shadow-sm whitespace-nowrap shrink-0 border transition-all bg-white text-blue-600 border-blue-200 hover:bg-blue-50">一時保存</button>
                                )}
                                <button onClick={sc.handleSendRequest} disabled={!sc.isReadyForRequest || sc.isSending} className={`px-5 py-2 font-bold rounded-lg text-sm flex items-center shadow-md whitespace-nowrap shrink-0 transition-all ${sc.isReadyForRequest ? 'bg-slate-800 text-white hover:bg-slate-900' : 'bg-slate-100 text-slate-300 cursor-not-allowed shadow-none'}`} title={sc.isReadyForRequest ? '' : '募集枠が未設定の日があります。'}>{sc.isSending ? <Loader2 size={16} className="animate-spin mr-2" /> : 'シフト回答依頼'}</button>
                                <div className="h-6 w-[1px] bg-slate-200 mx-1" />
                                <button onClick={sc.handleBatchDelete} disabled={sc.vacancies.length === 0} className={`px-4 py-2 font-bold rounded-lg text-xs flex items-center shadow-sm whitespace-nowrap shrink-0 border transition-all ${sc.vacancies.length > 0 ? 'bg-white text-red-500 border-red-200 hover:bg-red-50' : 'bg-gray-50 text-slate-300 border-gray-200 cursor-not-allowed'}`} title="この日の募集枠をすべて削除します。">一括削除</button>
                              </>
                          )}
                          {sc.currentStep === 1 && (
                              <button onClick={() => sc.goToStep(2)} className="px-5 py-2 font-bold rounded-lg text-sm flex items-center shadow-md whitespace-nowrap shrink-0 transition-all bg-slate-800 text-white hover:bg-slate-900">調整に進む</button>
                          )}
                          {sc.currentStep === 2 && sc.targetPeriod && (
                              <>
                                <button onClick={sc.handleAutoAssign} disabled={sc.isSending} data-testid="auto-assign" className="px-4 py-2 font-bold rounded-lg text-xs flex items-center gap-1.5 shadow-sm whitespace-nowrap shrink-0 border transition-all bg-blue-600 text-white border-blue-600 hover:bg-blue-700">{sc.isSending ? <Loader2 size={14} className="animate-spin" /> : <LayoutGrid size={14} />}もう一度自動作成</button>
                                {sc.autoResult && <button type="button" onClick={() => sc.setIsAutoResultOpen(true)} className="px-4 py-2 font-bold rounded-lg text-xs whitespace-nowrap shrink-0 border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">自動作成の結果</button>}
                                <button onClick={() => sc.goToStep(3)} className="px-5 py-2 font-bold rounded-lg text-sm flex items-center shadow-md whitespace-nowrap shrink-0 transition-all bg-slate-800 text-white hover:bg-slate-900">公開に進む</button>
                              </>
                          )}
                          {sc.currentStep === 3 && sc.targetPeriod && (
                              <button onClick={sc.handleConfirmShifts} disabled={sc.isSending} className={`px-5 py-2 font-bold rounded-lg text-sm flex items-center shadow-md whitespace-nowrap shrink-0 transition-all bg-green-600 text-white hover:bg-green-700`}>
                                {sc.isSending ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
                                シフト確定・通知
                              </button>
                          )}
                      </div>
                  </div>
              </div>
          </div>
      </div>

      {/* 期間全体の状況：募集ステップは「希望の集まり状況」、調整以降は「警告一覧」 */}
      {sc.targetPeriod && sc.currentStep >= 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {sc.currentStep === 1 ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="coverage-panel">
                      <div className="flex items-center justify-between gap-2 mb-2">
                          <h2 className="text-sm font-bold text-slate-800 whitespace-nowrap">希望の集まり状況</h2>
                          <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${sc.requestCoverage.length === 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>不足の見込み{sc.requestCoverage.length}枠</span>
                              {sc.requestCoverage.some(x => !x.v.openCall) && <button type="button" disabled={sc.isSending} onClick={() => sc.handleOpenCall(sc.requestCoverage.filter(x => !x.v.openCall).map(x => x.v.id))} className="text-xs font-bold text-white bg-slate-800 rounded-lg px-3 py-1.5 hover:bg-slate-900 whitespace-nowrap">追加募集する</button>}
                          </div>
                      </div>
                      {sc.requestCoverage.length === 0 ? (
                          <p className="text-xs text-slate-500">すべての枠で、どの時間帯も必要人数以上の希望が集まっています。</p>
                      ) : (
                          <ul className="max-h-40 overflow-y-auto divide-y divide-slate-100 text-xs">
                              {sc.requestCoverage.map(({ v, gaps }) => (
                                  <li key={v.id} className="py-1.5 flex items-center justify-between gap-2">
                                      <span className="text-slate-700 truncate">{v.start.slice(5, 10).replace('-', '/')} {sc.positions.find(p => p.id === v.positionId)?.name}：{gapsLabel(gaps)}が足りない見込み。{v.openCall ? `追加募集中、応募${sc.activeApplicantCount(v)}名。` : ''}</span>
                                      <button type="button" onClick={() => { const [y, m, d] = v.start.slice(0, 10).split('-').map(Number); sc.setCurrentDate(new Date(y, m - 1, d)); }} className="shrink-0 text-blue-600 font-bold hover:underline">該当日へ</button>
                                  </li>
                              ))}
                          </ul>
                      )}
                  </div>
              ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="warnings-panel">
                  <div className="flex items-center justify-between gap-2 mb-2">
                      <h2 className="text-sm font-bold text-slate-800 whitespace-nowrap">警告一覧（期間全体）</h2>
                      <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sc.warnings.length === 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{sc.warnings.length}件</span>
                          {sc.shortageVacancies.some(x => !x.v.openCall) && <button type="button" disabled={sc.isSending} onClick={() => sc.handleOpenCall(sc.shortageVacancies.filter(x => !x.v.openCall).map(x => x.v.id))} data-testid="open-call-shortages" className="text-xs font-bold text-white bg-slate-800 rounded-lg px-3 py-1.5 hover:bg-slate-900 whitespace-nowrap">不足枠を追加募集</button>}
                      </div>
                  </div>
                  {sc.warnings.length === 0 ? (
                      <p className="text-xs text-slate-500">人数不足・業務の対応者なし・相性NG・ペア不在・上限超え・連勤・経験者不在はありません。</p>
                  ) : (
                      <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 text-xs">
                          {warningGroups.map(g => {
                              const open = openWarningDay === g.key;
                              return (
                                  <li key={g.key} data-testid="warning-day">
                                      <div className="py-1.5 flex items-center justify-between gap-2">
                                          <button type="button" onClick={() => setOpenWarningDay(open ? null : g.key)} aria-expanded={open} className="flex items-center gap-2 min-w-0 flex-wrap text-left">
                                              <span className="font-bold text-slate-800 whitespace-nowrap w-[72px] shrink-0">{g.label}</span>
                                              {g.kinds.map(k => <span key={k.kind} className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${WARNING_STYLE[k.kind].cls}`}>{WARNING_STYLE[k.kind].label} {k.count}件</span>)}
                                          </button>
                                          {g.date && <button type="button" onClick={() => { const [y, m, d] = g.date!.split('-').map(Number); sc.setCurrentDate(new Date(y, m - 1, d)); }} className="shrink-0 text-blue-600 font-bold hover:underline whitespace-nowrap">該当日へ</button>}
                                      </div>
                                      {open && (
                                          <ul className="pb-2 pl-[80px] space-y-1">
                                              {g.items.map((w, i) => <li key={i} className="text-slate-600 leading-relaxed">{w.message.replace(/^\d{2}\/\d{2} /, '')}</li>)}
                                          </ul>
                                      )}
                                  </li>
                              );
                          })}
                      </ul>
                  )}
              </div>
              )}
              <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="unsubmitted-panel">
                  <div className="flex items-center justify-between mb-2">
                      <h2 className="text-sm font-bold text-slate-800">希望が未提出のスタッフ</h2>
                      <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sc.unsubmittedStaff.length === 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{sc.unsubmittedStaff.length}名</span>
                          {sc.unsubmittedStaff.length > 0 && <button type="button" onClick={() => sc.handleRemind(sc.unsubmittedStaff.map(st => st.id))} className="text-xs font-bold text-white bg-slate-800 rounded-lg px-3 py-1.5 hover:bg-slate-900 whitespace-nowrap">全員に催促</button>}
                      </div>
                  </div>
                  {sc.unsubmittedStaff.length === 0 ? (
                      <p className="text-xs text-slate-500">全員の希望が提出されています。</p>
                  ) : (
                      <ul className="max-h-40 overflow-y-auto flex flex-wrap gap-2 text-xs">
                          {sc.unsubmittedStaff.map(st => (
                              <li key={st.id} className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-2 py-1">
                                  <span className="font-bold text-slate-700">{st.lastName} {st.firstName}</span>
                                  <button type="button" onClick={() => sc.handleRemind([st.id])} className="text-blue-600 font-bold hover:underline">催促</button>
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
          </div>
      )}

      {sc.targetPeriod && sc.currentStep >= 2 && sc.dayDutyStatus.some(x => x.duty.requiredDaily) && (
          <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="day-duty-status">
              <span className="text-xs font-bold text-slate-500 whitespace-nowrap">この日の必須業務</span>
              {sc.dayDutyStatus.filter(x => x.duty.requiredDaily).map(({ duty, holders }) => (
                  <span key={duty.id} className="flex items-center gap-1.5 text-xs whitespace-nowrap" title={holders.length > 0 ? `${duty.name}：${holders.map(h => `${h.lastName} ${h.firstName}`).join('、')}` : duty.name}>
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-[3px] rounded-[3px] border border-blue-300 bg-blue-50 text-blue-700 text-[10px] font-bold">{dutyMark(duty)}</span>
                      <span className="font-bold text-slate-700">{duty.name}</span>
                      {holders.length > 0
                          ? <span className="font-bold text-green-700">{holders.map(h => h.lastName).join('・')}</span>
                          : <span className="font-bold text-red-600">対応者なし</span>}
                  </span>
              ))}
          </div>
      )}

      <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col relative ${sc.isRefreshing ? 'opacity-60 pointer-events-none' : ''}`}>
          <ShiftCreationTimeline
              currentStep={sc.currentStep} isClosed={false}
              positions={sc.positions.filter(p => p.storeIds.includes(sc.selectedStoreId))}
              vacancies={sc.vacancies} requests={sc.requests} assignedShifts={sc.assignedShifts} allStaff={sc.allStaff}
              editDrag={sc.editDrag} createDrag={sc.createDrag} events={sc.events}
              handleMouseDown={sc.handleMouseDown} handleMouseEnter={sc.handleMouseEnter} handleMouseUp={sc.handleMouseUp} handleEditDragStart={sc.handleEditDragStart}
              setSelectedVacancyForCandidates={sc.setSelectedVacancyForCandidates} setIsCandidateModalOpen={sc.setIsCandidateModalOpen}
              openEditModal={(data) => { sc.setEditingVacancy(data); sc.setIsEditModalOpen(true); }}
              onDeleteVacancy={sc.handleDeleteVacancy}
              onEventClick={(d, e) => {
                sc.setEditingEvent(e || { id: `event_${Date.now()}`, storeId: sc.selectedStoreId, date: d, isAllDay: true });
                sc.setIsEventModalOpen(true);
              }}
              currentDate={sc.currentDate} getBorderColorClass={getBorderColorClass}
              onAddPosition={() => setIsAddPositionOpen(true)}
              onRemovePosition={sc.removePosition}
              markDuties={sc.storeDuties.filter(d => d.requiredDaily)}
              applicantCount={sc.activeApplicantCount}
              respondentCount={sc.respondentCount}
              gapsOf={sc.gapsOf}
              baseStartTime={sc.activeStore?.baseStartTime} baseEndTime={sc.activeStore?.baseEndTime} isHoliday={sc.isTodayHoliday}
          />
      </div>

      <Modal isOpen={sc.isCandidateModalOpen} onClose={() => sc.setIsCandidateModalOpen(false)} title={sc.currentStep < 2 ? '回答したスタッフ' : 'スタッフの割り当て'} maxWidth="max-w-2xl">
          {sc.selectedVacancyForCandidates && (() => {
              // 最新の募集枠（応募・打診の状況）を反映する
              const v = sc.periodVacancies.find(x => x.id === sc.selectedVacancyForCandidates!.id) || sc.selectedVacancyForCandidates;
              const pos = sc.positions.find(p => p.id === v.positionId);
              const cands = sc.candidatesFor(v);
              const gaps = sc.gapsOf(v);
              // 募集の段階は回答の確認のみ（割り当ては調整の段階で行う）
              const readOnly = sc.currentStep < 2;
              const offList = cands.filter(c => c.kind === 'ng' && c.reasons.includes('休み希望'));
              const GROUPS: Array<{ kind: string; title: string; note?: string }> = readOnly ? [
                  { kind: 'request', title: 'この時間帯に希望を出したスタッフ' },
                  { kind: 'applicant', title: '追加募集・打診に応募したスタッフ' },
              ] : [
                  { kind: 'assigned', title: '割り当て済み' },
                  { kind: 'applicant', title: '追加募集・打診に応募したスタッフ' },
                  { kind: 'request', title: 'この時間帯に希望を出しているスタッフ' },
                  { kind: 'available', title: '希望はないが入れるスタッフ', note: '休み希望がなく、ポジション・勤務上限・連勤の条件を満たしています。打診して応募を待つか、了承済みなら直接割り当てます。' },
                  { kind: 'help', title: '他店舗のスタッフ（ヘルプ）', note: 'このポジションを担当できる他店舗のスタッフです。' },
                  { kind: 'ng', title: '条件に合わないスタッフ' },
              ];
              const timeOf = rangeLabel; // 夜をまたぐ場合は「翌」を付ける
              return (
                  <div className="space-y-4 pt-2" data-testid="candidate-modal">
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                          <div className="text-sm font-bold text-slate-800">{sc.formatDateJP(new Date(v.start.split('T')[0]))} {pos?.name}</div>
                          <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-600 tabular-nums whitespace-nowrap">{timeOf(v)}　必要{v.headcount || 1}名</span>
                              {!readOnly && <span data-testid="slot-gaps" className={`text-xs font-bold tabular-nums ${gaps.length === 0 ? 'text-green-700' : 'text-red-600'}`}>{gaps.length === 0 ? 'どの時間帯も充足' : `${gapsLabel(gaps)}不足`}</span>}
                              {!readOnly && gaps.length > 0 && (v.openCall
                                  ? <span className="text-[11px] font-bold text-orange-600 border border-orange-200 bg-orange-50 rounded px-2 py-0.5 whitespace-nowrap">追加募集中</span>
                                  : <button type="button" onClick={() => sc.handleOpenCall([v.id])} className="text-[11px] font-bold text-white bg-slate-800 rounded px-2 py-1 whitespace-nowrap hover:bg-slate-900">この枠を追加募集</button>)}
                          </div>
                      </div>
                      <div className="max-h-[56vh] overflow-y-auto space-y-3">
                          {GROUPS.map(g => {
                              const list = cands.filter(c => c.kind === g.kind);
                              if (list.length === 0) return null;
                              return (
                                  <div key={g.kind} data-testid={`cand-group-${g.kind}`}>
                                      <p className="text-xs font-bold text-slate-500 mb-1">{g.title}（{list.length}名）</p>
                                      {g.note && <p className="text-[11px] text-slate-400 mb-1">{g.note}</p>}
                                      <ul className={`divide-y divide-slate-100 border border-slate-200 rounded-xl ${g.kind === 'ng' ? 'opacity-60' : ''}`}>
                                          {list.map(c => (
                                              <li key={c.staff.id} className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 py-2 ${c.kind === 'assigned' ? 'bg-green-50/60' : ''}`}>
                                                  <div className="min-w-0">
                                                      <div className="flex flex-wrap items-center gap-x-1.5 text-sm font-bold text-slate-800">
                                                          {c.staff.lastName} {c.staff.firstName}
                                                          <DutyMarks staff={c.staff} duties={sc.storeDuties} />
                                                          <span className="text-[10px] font-medium text-slate-400">レベル{c.level}・優先度{c.staff.priority === 'high' ? '高' : c.staff.priority === 'medium' ? '中' : '低'}</span>
                                                      </div>
                                                      <div className="text-[11px] text-slate-500 tabular-nums">
                                                          {c.assignedShift ? `割当 ${timeOf(c.assignedShift)}` : c.request ? `希望 ${timeOf(c.request)}${c.request.preference === 'triangle' ? '（どちらでも）' : ''}` : c.kind === 'applicant' ? '応募あり' : c.isHelp ? `所属 ${sc.stores.find(st => st.id === c.staff.stores?.[0])?.name || '他店舗'}` : '希望なし'}
                                                          {c.staff.monthlyWorkHourLimit ? `　月間 ${c.hours.toFixed(1)}／${c.staff.monthlyWorkHourLimit}時間` : `　月間 ${c.hours.toFixed(1)}時間`}
                                                          {c.offered && c.kind !== 'applicant' && c.kind !== 'assigned' ? '　打診済み（返答待ち）' : ''}
                                                      </div>
                                                      {c.pairNote && c.kind !== 'ng' && <div className={`text-[11px] font-bold ${c.pairNote.includes('いません') ? 'text-orange-600' : 'text-green-700'}`}>{c.pairNote}</div>}
                                                      {c.kind === 'ng' && <div className="text-[11px] font-bold text-red-500">{c.reasons.join('、')}</div>}
                                                      {!readOnly && c.ok && <div className="text-[11px] font-bold text-blue-700 tabular-nums">割り当てると {fmtMinutes(c.overlapStart)}〜{fmtMinutes(c.overlapEnd)}の勤務です。{c.gain > 0 ? `不足を${Math.round(c.gain / 6) / 10}時間埋めます。` : 'この時間に不足はありません。'}</div>}
                                                  </div>
                                                  {!readOnly && <div className="flex items-center gap-1.5 shrink-0">
                                                      {c.kind === 'assigned' && <button type="button" onClick={() => sc.handleUnassign(v, c.staff.id)} className="text-xs font-bold text-slate-600 border border-slate-300 bg-white rounded-lg px-3 py-1.5 hover:bg-slate-50 whitespace-nowrap">解除</button>}
                                                      {(c.kind === 'available' || c.kind === 'help') && (
                                                          <button type="button" disabled={c.offered || sc.offeringKey === `${v.id}:${c.staff.id}`} onClick={() => sc.handleOffer(v, c.staff.id)} className={`text-xs font-bold rounded-lg px-3 py-1.5 whitespace-nowrap border ${c.offered ? 'text-slate-400 border-slate-200 bg-slate-50' : 'text-blue-600 border-blue-300 bg-white hover:bg-blue-50'}`}>{c.offered ? '打診済み' : '打診'}</button>
                                                      )}
                                                      {(c.kind === 'request' || c.kind === 'applicant' || c.kind === 'available' || c.kind === 'help') && (
                                                          <button type="button" onClick={() => sc.handleAssign(v, c.staff.id)} className="text-xs font-bold rounded-lg px-3 py-1.5 whitespace-nowrap bg-blue-600 text-white hover:bg-blue-700">割り当て</button>
                                                      )}
                                                  </div>}
                                              </li>
                                          ))}
                                      </ul>
                                  </div>
                              );
                          })}
                          {readOnly && offList.length > 0 && (
                              <div data-testid="cand-group-off">
                                  <p className="text-xs font-bold text-slate-500 mb-1">休み希望のスタッフ（{offList.length}名）</p>
                                  <p className="text-sm text-slate-600 border border-slate-200 rounded-xl px-3 py-2">{offList.map(c => `${c.staff.lastName} ${c.staff.firstName}`).join('、')}</p>
                              </div>
                          )}
                          {readOnly && cands.filter(c => c.kind === 'request' || c.kind === 'applicant').length === 0 && (
                              <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-xl px-3 py-4 text-center">この時間帯に希望を出したスタッフはまだいません。</p>
                          )}
                      </div>
                      <div className="flex justify-end">
                          <button onClick={() => sc.setIsCandidateModalOpen(false)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded transition-colors">閉じる</button>
                      </div>
                  </div>
              );
          })()}
      </Modal>

      <Modal isOpen={sc.isAutoResultOpen && !!sc.autoResult} onClose={() => sc.setIsAutoResultOpen(false)} title="自動作成の結果" maxWidth="max-w-xl">
          {sc.autoResult && (() => {
              const r = sc.autoResult;
              const rate = r.totalNeed > 0 ? Math.floor((r.filled / r.totalNeed) * 100) : 100;
              const missingPeople = r.shortages.reduce((acc, x) => acc + x.missing, 0);
              const notOpened = r.shortages.filter(x => !(sc.periodVacancies.find(v => v.id === x.v.id)?.openCall));
              return (
                  <div className="space-y-5 pt-2" data-testid="auto-result">
                      <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="border border-slate-200 rounded-xl p-3"><p className="text-[11px] font-bold text-slate-500">割り当て</p><p className="text-2xl font-black text-slate-800">{r.assignedCount}<span className="text-xs ml-0.5">件</span></p></div>
                          <div className="border border-slate-200 rounded-xl p-3"><p className="text-[11px] font-bold text-slate-500">充足率（人数×時間）</p><p className={`text-2xl font-black ${rate >= 95 ? 'text-green-600' : 'text-orange-600'}`}>{rate}<span className="text-xs ml-0.5">%</span></p></div>
                          <div className="border border-slate-200 rounded-xl p-3"><p className="text-[11px] font-bold text-slate-500">不足</p><p className={`text-2xl font-black ${missingPeople === 0 ? 'text-green-600' : 'text-red-600'}`}>{r.shortages.length}<span className="text-xs ml-0.5">枠</span></p></div>
                      </div>
                      {r.shortages.length === 0 && r.dutyMissing.length === 0 ? (
                          <p className="text-sm text-slate-600">すべての募集枠が埋まりました。内容を確認して「公開に進む」から確定してください。</p>
                      ) : (
                          <>
                              <div>
                                  <p className="text-sm font-bold text-slate-800 mb-2">足りない枠への対応</p>
                                  <ol className="text-xs text-slate-600 leading-relaxed space-y-1 list-decimal pl-5">
                                      <li>不足枠をまとめて追加募集し、スタッフからの応募を待ちます（応募した人は候補一覧の上に表示されます）。</li>
                                      <li>枠を開いて、希望はないが入れるスタッフや他店舗のスタッフに打診します。</li>
                                      <li>どうしても埋まらない枠は、必要人数を見直すか、管理者が入ります。</li>
                                  </ol>
                              </div>
                              {r.dutyMissing.length > 0 && (
                                  <p className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">毎日必要な業務の対応者がいない日が{r.dutyMissing.length}件あります（{Array.from(new Set(r.dutyMissing.map(x => x.duty.name))).join('、')}）。警告一覧から該当日を確認してください。</p>
                              )}
                              <ul className="max-h-40 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl text-xs">
                                  {r.shortages.map(({ v, gaps }) => (
                                      <li key={v.id} className="px-3 py-1.5 flex items-center justify-between gap-2">
                                          <span className="text-slate-700">{v.start.slice(5, 10).replace('-', '/')} {sc.positions.find(p => p.id === v.positionId)?.name}：{gapsLabel(gaps)}不足。</span>
                                          <button type="button" onClick={() => { sc.setIsAutoResultOpen(false); sc.setSelectedVacancyForCandidates(v); sc.setIsCandidateModalOpen(true); }} className="shrink-0 text-blue-600 font-bold hover:underline">候補を見る</button>
                                      </li>
                                  ))}
                              </ul>
                          </>
                      )}
                      <div className="flex justify-end gap-2 pt-2 border-t">
                          <button type="button" onClick={() => sc.setIsAutoResultOpen(false)} className="px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded">あとで調整する</button>
                          {notOpened.length > 0 && <button type="button" disabled={sc.isSending} onClick={async () => { await sc.handleOpenCall(notOpened.map(x => x.v.id)); sc.setIsAutoResultOpen(false); }} data-testid="auto-result-open-call" className="px-5 py-2.5 bg-slate-800 text-white font-bold rounded hover:bg-slate-900">不足枠を追加募集する（{notOpened.length}枠）</button>}
                      </div>
                  </div>
              );
          })()}
      </Modal>

      <Modal isOpen={sc.isEditModalOpen} onClose={() => sc.setIsEditModalOpen(false)} title="募集枠編集" maxWidth="max-w-md">
          {sc.editingVacancy && (
              <div className="space-y-6 pt-4">
                  <div>
                      <label className="text-xs font-bold text-slate-500 mb-1.5 block">時間</label>
                      <div className="flex items-center gap-2">
                          <input type="time" value={sc.editingVacancy.start?.split('T')[1].slice(0,5)} onChange={e => { const date = sc.editingVacancy.start?.split('T')[0]; sc.setEditingVacancy({...sc.editingVacancy, start: `${date}T${e.target.value}:00`}); }} className="w-full border p-2 rounded" />
                          <span>〜</span>
                          <input type="time" value={sc.editingVacancy.end?.split('T')[1].slice(0,5)} onChange={e => { const date = sc.editingVacancy.end?.split('T')[0]; sc.setEditingVacancy({...sc.editingVacancy, end: `${date}T${e.target.value}:00`}); }} className="w-full border p-2 rounded" />
                      </div>
                  </div>
                  <div>
                      <label className="text-xs font-bold text-slate-500 mb-1.5 block">必要人数</label>
                      <div className="flex items-center gap-3">
                        <input type="number" step="1" value={sc.editingVacancy.headcount || 1} min={1} onChange={e => sc.setEditingVacancy({...sc.editingVacancy, headcount: Math.max(1, Math.round(Number(e.target.value))) })} className="w-24 border p-2 rounded font-bold text-lg" />
                        <span className="text-xs text-slate-400 font-bold">名（この時間帯に必要な人数）</span>
                      </div>
                  </div>
                  <div className="pt-6 flex justify-between gap-3 border-t">
                      <button onClick={() => sc.handleDeleteVacancy()} className="text-red-500 font-bold px-4 hover:bg-red-50 rounded">削除</button>
                      <div className="flex gap-2">
                          <button onClick={() => sc.setIsEditModalOpen(false)} className="px-4 py-2 text-slate-500 font-bold">キャンセル</button>
                          <button onClick={sc.handleSaveVacancyDetail} className="px-6 py-2 bg-blue-600 text-white font-bold rounded shadow-md">保存</button>
                      </div>
                  </div>
              </div>
          )}
      </Modal>

      <Modal isOpen={isAddPositionOpen} onClose={() => setIsAddPositionOpen(false)} title="ポジションの追加" maxWidth="max-w-md">
        <div className="space-y-6 pt-2">
            <Input label="ポジション名" value={newPositionName} placeholder="例：宴会サービス、ベーカリー" onChange={(e) => setNewPositionName(e.target.value)} />
            <div>
                <p className="text-sm font-bold text-slate-700 mb-2">色</p>
                <div className="flex flex-wrap gap-2">
                    {POSITION_COLORS.map(c => (
                        <button key={c} type="button" onClick={() => setNewPositionColor(c)} aria-label="色を選択"
                            className={`w-8 h-8 rounded-full border-2 ${c.split(' ')[0]} ${newPositionColor === c ? 'border-blue-600 ring-2 ring-blue-200' : 'border-white shadow'}`} />
                    ))}
                </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">追加したポジションの行で、時間帯をドラッグすると募集枠を作成できます。</p>
            <div className="pt-4 flex justify-end gap-3 border-t">
                <button onClick={() => setIsAddPositionOpen(false)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded transition-colors">キャンセル</button>
                <button onClick={handleAddPosition} disabled={!newPositionName.trim()} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded shadow-md hover:bg-blue-700 transition-all disabled:bg-slate-300">追加</button>
            </div>
        </div>
      </Modal>

      <Modal isOpen={sc.isSavePatternModalOpen} onClose={() => sc.setIsSavePatternModalOpen(false)} title="モデルシフトとして保存" maxWidth="max-w-md">
        <div className="space-y-6 pt-2">
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <p className="text-xs text-blue-800 font-bold leading-relaxed">
                    現在の募集枠の構成に名前を付けて保存します。<br/>
                    保存したモデルシフトは、設定画面で適用する曜日を指定すると、期間指定で一括作成する際に自動で反映されます。
                </p>
            </div>

            <Input
                label="モデルシフト名"
                value={sc.newPatternName}
                placeholder="例：月曜通常、ランチピーク"
                onChange={(e) => sc.setNewPatternName(e.target.value)}
            />

            <div className="pt-4 flex justify-end gap-3 border-t">
                <button onClick={() => sc.setIsSavePatternModalOpen(false)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded transition-colors">キャンセル</button>
                <button
                    onClick={sc.handleSaveAsPattern}
                    disabled={!sc.newPatternName.trim() || sc.isLoading}
                    className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded shadow-md hover:bg-blue-700 transition-all flex items-center"
                >
                    {sc.isLoading && <Loader2 className="animate-spin mr-2" size={16} />}
                    <Save size={16} className="mr-2" />
                    この内容で保存
                </button>
            </div>
        </div>
      </Modal>

      <Modal isOpen={sc.isEventModalOpen} onClose={() => sc.setIsEventModalOpen(false)} title="行事設定" maxWidth="max-w-md">
        <div className="space-y-6 pt-2">
            <Input
                label="行事名"
                value={sc.editingEvent.title || ''}
                placeholder="例：全体会議、特別清掃"
                onChange={(e) => sc.setEditingEvent({...sc.editingEvent, title: e.target.value})}
            />

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-bold text-slate-500 mb-1.5 block">開始日</label>
                    <input
                        type="date"
                        value={sc.editingEvent.date || ''}
                        onChange={(e) => sc.setEditingEvent({...sc.editingEvent, date: e.target.value, endDate: sc.editingEvent.endDate || e.target.value})}
                        className="w-full border border-slate-300 rounded-[5px] p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 mb-1.5 block">終了日</label>
                    <input
                        type="date"
                        value={sc.editingEvent.endDate || sc.editingEvent.date || ''}
                        min={sc.editingEvent.date}
                        onChange={(e) => sc.setEditingEvent({...sc.editingEvent, endDate: e.target.value})}
                        className="w-full border border-slate-300 rounded-[5px] p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-100"
                    />
                </div>
            </div>

            <div className="pt-6 flex justify-end gap-3 border-t">
                {sc.editingEvent.id && sc.events.some(e => e.id === sc.editingEvent.id) && (
                  <button
                      onClick={sc.handleDeleteEvent}
                      className="mr-auto px-4 py-2.5 text-red-500 font-bold hover:bg-red-50 rounded-[5px] transition-colors"
                  >
                      削除
                  </button>
                )}
                <button
                    onClick={() => sc.setIsEventModalOpen(false)}
                    className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-[5px] transition-colors"
                >
                    キャンセル
                </button>
                <button
                    onClick={sc.handleSaveEvent}
                    disabled={sc.isEventSaving}
                    className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[5px] shadow-md hover:bg-blue-700 transition-all flex items-center"
                >
                    {sc.isEventSaving && <Loader2 className="animate-spin mr-2" size={16} />}
                    保存
                </button>
            </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShiftCreationPage;
